import type { MouseEvent } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { OpenInNew, SWReactIcons } from "@similarweb/icons";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { numberFilter } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";

import { ensureProtocol } from "../utils/utils";

import { AdCardImage } from "./AdCardImage";
import type { AdsGalleryData } from "./hooks/useAdsGallery";
import { creativeTypeToInfoMap } from "./hooks/useAdsGallery";
import {
    AdCard,
    AdCardFooter,
    AdCardFooterInner,
    CreativeTypeIconWrapper,
    Favicon,
    FaviconContainer,
    LinkOutIconWrapper,
    FooterText,
    NativeAdIcon,
    LeftFlexInlineRow,
    CreativeTypeText,
} from "./StyledComponents";

const MIN_BANNER_ICON_SIZE = 6;
const MAX_BANNER_ICON_SIZE = 16;

export const getCreativeTypeIcon = (
    creativeType: AdsGalleryData["creativeType"],
    dimensions: AdsGalleryData["dimensions"],
) => {
    const [width, height] = dimensions.split("x").map(Number);
    if (creativeType === "banner" && width && height) {
        const normalizedWidth = Math.min(
            Math.max(MIN_BANNER_ICON_SIZE, (width / height) * MAX_BANNER_ICON_SIZE),
            MAX_BANNER_ICON_SIZE,
        );
        const normalizedHeight = Math.min(
            Math.max(MIN_BANNER_ICON_SIZE, (height / width) * MAX_BANNER_ICON_SIZE),
            MAX_BANNER_ICON_SIZE,
        );
        return (
            <NativeAdIcon
                width={normalizedWidth}
                height={normalizedHeight}
            />
        );
    } else {
        return (
            <SWReactIcons
                iconName={creativeTypeToInfoMap[creativeType].icon}
                size="xs"
                iconColor={colorsPalettes.carbon[200]}
            />
        );
    }
};

type IAdIntelligenceAdCardProps = AdsGalleryData & {
    onClick: () => void;
    hideDuration?: boolean;
};

export const AdIntelligenceAdCard = (props: IAdIntelligenceAdCardProps) => {
    const { translate } = useTranslate();
    const [width, height] = props.dimensions.split("x").map(Number);
    const dimensions = props.dimensions?.split("x");
    const dimensionsFormatted = dimensions.join(" X ");
    const firstSeen = dayjs(props.firstSeen).format(shortMonthDayAndYear);
    const lastSeen = dayjs(props.lastSeen).format(shortMonthDayAndYear);
    const activeDaysFormatted = `${numberFilter()(props.activeDays)} ${translate(
        props.activeDays > 1
            ? "ad.intelligence.advertiser.ad.modal.details.duration.days"
            : "ad.intelligence.advertiser.ad.modal.details.duration.day",
    )}`;

    const hasLandingPage = Boolean(props.landingPagesLinks?.[0]);
    const landingPageHref = hasLandingPage ? ensureProtocol(props.landingPagesLinks[0]) : undefined;

    const handleLandingPageClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (!hasLandingPage) {
            event.preventDefault();
            return;
        }
        allTrackers.dispatchTrackEvent("External Link", "click", "Ads/URL/Preview");
    };

    const isVideo = props.creativeType === "video";

    return (
        <AdCard data-automation="ad-card">
            <AdCardImage
                src={props.creativeImageUri}
                width={width}
                height={height}
                onClick={props.onClick}
                isVideo={isVideo}
            />
            <AdCardFooter>
                {props.creativeType ? (
                    <AdCardFooterInner>
                        {props?.advertiserFavicon && "advertiser" in props ? (
                            <Tooltip
                                content={translate("ad.intelligence.advertiser.ad.card.advertiser.tooltip", {
                                    domain: props.advertiser,
                                })}
                            >
                                <FaviconContainer>
                                    <Favicon src={props.advertiserFavicon} />
                                </FaviconContainer>
                            </Tooltip>
                        ) : null}
                        <Tooltip content={translate(creativeTypeToInfoMap[props.creativeType].tooltip)}>
                            <LeftFlexInlineRow>
                                <CreativeTypeIconWrapper
                                    data-automation-ad-type={translate(creativeTypeToInfoMap[props.creativeType].name)}
                                >
                                    {getCreativeTypeIcon(props.creativeType, props.dimensions)}
                                </CreativeTypeIconWrapper>
                                <CreativeTypeText>
                                    {translate(creativeTypeToInfoMap[props.creativeType].name)}
                                </CreativeTypeText>
                            </LeftFlexInlineRow>
                        </Tooltip>
                        <Tooltip
                            content={translate("ad.intelligence.advertiser.ad.card.dimensions.tooltip", {
                                dimensions: dimensionsFormatted,
                            })}
                        >
                            <LeftFlexInlineRow>
                                <CreativeTypeIconWrapper>
                                    <SWReactIcons
                                        iconName="dimensions"
                                        size="xs"
                                        iconColor={colorsPalettes.carbon[200]}
                                    />
                                </CreativeTypeIconWrapper>
                                <FooterText>{dimensionsFormatted}</FooterText>
                            </LeftFlexInlineRow>
                        </Tooltip>
                        {!props.hideDuration ? (
                            <Tooltip
                                content={
                                    <>
                                        <span>
                                            {translate("ad.intelligence.advertiser.ad.card.duration.tooltip.p1", {
                                                activeDays: activeDaysFormatted,
                                            })}
                                        </span>
                                        <br />
                                        <span>
                                            {translate("ad.intelligence.advertiser.ad.card.duration.tooltip.p2", {
                                                firstSeen,
                                            })}
                                        </span>
                                        <br />
                                        <span>
                                            {translate("ad.intelligence.advertiser.ad.card.duration.tooltip.p3", {
                                                lastSeen,
                                            })}
                                        </span>
                                    </>
                                }
                            >
                                <LeftFlexInlineRow
                                    data-automation-active-days={activeDaysFormatted}
                                    data-automation-first-seen={firstSeen}
                                    data-automation-last-seen={lastSeen}
                                >
                                    <CreativeTypeIconWrapper>
                                        <SWReactIcons
                                            iconName="month"
                                            size="xs"
                                            iconColor={colorsPalettes.carbon[200]}
                                        />
                                    </CreativeTypeIconWrapper>
                                    <FooterText>{activeDaysFormatted}</FooterText>
                                </LeftFlexInlineRow>
                            </Tooltip>
                        ) : null}
                    </AdCardFooterInner>
                ) : null}
                <Tooltip
                    content={translate(
                        hasLandingPage
                            ? "ad.intelligence.advertiser.ad.card.link.out"
                            : "ad.intelligence.advertiser.ad.card.link.out.disabled",
                    )}
                >
                    <LinkOutIconWrapper
                        $disabled={!hasLandingPage}
                        href={landingPageHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleLandingPageClick}
                        aria-disabled={!hasLandingPage}
                    >
                        <OpenInNew
                            size="xs"
                            color={hasLandingPage ? colorsPalettes.blue[400] : colorsPalettes.carbon[300]}
                        />
                    </LinkOutIconWrapper>
                </Tooltip>
            </AdCardFooter>
        </AdCard>
    );
};
